// Tokenized Maya guide invite landing.
//
// Flow:
//  1. Resolve the invite token via the public server fn (no auth needed).
//  2. If revoked/expired/not-found: show a friendly error.
//  3. If the visitor is signed in with the invited email, accept and redirect
//     straight to `/tour/{tourSlug}` (or /maya when no tour is associated).
//  4. Otherwise send them to `/auth?next=/maya/invite/{token}` so the sign-in
//     flow bounces them right back here after authentication.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Sparkles, AlertCircle, MailCheck } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import {
  resolveMayaInvite,
  acceptMayaInvite,
  type MayaInvitePreview,
} from "@/lib/maya-access.functions";
import { useAuth, signOut } from "@/hooks/use-auth";

export const Route = createFileRoute("/maya/invite/$token")({
  head: () => ({
    meta: [
      { title: "Tu guía Maya · Invitación" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: MayaInvitePage,
});

function MayaInvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const { ready, user } = useAuth();
  const resolveFn = useServerFn(resolveMayaInvite);
  const acceptFn = useServerFn(acceptMayaInvite);

  const preview = useQuery({
    queryKey: ["maya-invite", token],
    queryFn: () => resolveFn({ data: { token } }),
    retry: false,
  });

  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invite: MayaInvitePreview | undefined = preview.data;
  const callerEmail = user?.email?.toLowerCase() ?? null;
  const inviteEmail = invite?.email?.toLowerCase() ?? null;
  const emailMatches = !!callerEmail && !!inviteEmail && callerEmail === inviteEmail;

  // Auto-accept as soon as we have a signed-in user with matching email.
  useEffect(() => {
    if (!ready || accepting) return;
    if (!invite || invite.status !== "valid") return;
    if (!user) return;
    if (!emailMatches) return;
    let cancelled = false;
    setAccepting(true);
    acceptFn({ data: { token } })
      .then((res) => {
        if (cancelled) return;
        if ("error" in res) {
          setError(res.error);
          setAccepting(false);
          return;
        }
        const dest = res.tourSlug ? `/tour/${res.tourSlug}` : "/maya";
        navigate({ to: dest, replace: true });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        setAccepting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ready, user, invite, emailMatches, accepting, acceptFn, navigate, token]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto flex min-h-[70vh] max-w-2xl flex-col justify-center px-4 py-16 sm:px-6">
        {preview.isLoading || !ready ? (
          <div className="grid place-items-center gap-3 py-20">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Validando tu invitación…</p>
          </div>
        ) : !invite || invite.status === "not_found" ? (
          <InviteError
            title="Invitación no encontrada"
            message="Este enlace no es válido o ya no está disponible. Verifica el correo original o solicita uno nuevo."
          />
        ) : invite.status === "revoked" ? (
          <InviteError
            title="Invitación revocada"
            message="Este acceso fue retirado por el administrador. Escríbenos si crees que fue por error."
          />
        ) : invite.status === "expired" ? (
          <InviteError
            title="Invitación expirada"
            message={`Este enlace expiró${invite.expiresAt ? ` el ${new Date(invite.expiresAt).toLocaleDateString("es-MX", { dateStyle: "long" })}` : ""}. Solicita uno nuevo si aún deseas acceder.`}
          />
        ) : !user ? (
          <SignInPrompt token={token} email={invite.email ?? ""} tourTitle={invite.tourTitle} />
        ) : !emailMatches ? (
          <EmailMismatchPrompt
            expectedEmail={invite.email ?? ""}
            currentEmail={user.email ?? ""}
          />
        ) : (
          <div className="grid place-items-center gap-4 py-20 text-center">
            <div className="grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
              {accepting ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <Sparkles className="h-6 w-6" />
              )}
            </div>
            <h1 className="font-display text-2xl font-semibold">
              Abriendo tu guía{invite.tourTitle ? ` de ${invite.tourTitle}` : ""}…
            </h1>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}

function InviteError({ title, message }: { title: string; message: string }) {
  return (
    <div className="grid place-items-center gap-4 rounded-3xl border border-dashed border-border bg-card/50 px-6 py-16 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-destructive/10 text-destructive">
        <AlertCircle className="h-6 w-6" />
      </div>
      <h1 className="font-display text-2xl font-semibold">{title}</h1>
      <p className="max-w-md text-muted-foreground">{message}</p>
      <Link
        to="/maya"
        className="mt-2 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
      >
        Ir a Maya
      </Link>
    </div>
  );
}

function SignInPrompt({
  token,
  email,
  tourTitle,
}: {
  token: string;
  email: string;
  tourTitle: string | null;
}) {
  const nextPath = `/maya/invite/${token}`;
  return (
    <div className="grid place-items-center gap-4 rounded-3xl border border-border bg-card px-6 py-14 text-center shadow-sm">
      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
        <MailCheck className="h-6 w-6" />
      </div>
      <h1 className="font-display text-2xl font-semibold">
        Tienes una guía Maya esperándote
      </h1>
      <p className="max-w-md text-muted-foreground">
        {tourTitle
          ? `Se te otorgó acceso a la guía IA de ${tourTitle}.`
          : "Se te otorgó acceso a Maya Premium."}{" "}
        Inicia sesión con <strong>{email}</strong> para abrirla.
      </p>
      <a
        href={`/auth?next=${encodeURIComponent(nextPath)}&email=${encodeURIComponent(email)}`}
        className="mt-2 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
      >
        Iniciar sesión y abrir mi guía
      </a>
    </div>
  );
}

function EmailMismatchPrompt({
  expectedEmail,
  currentEmail,
}: {
  expectedEmail: string;
  currentEmail: string;
}) {
  return (
    <div className="grid place-items-center gap-4 rounded-3xl border border-amber-400/50 bg-amber-50 px-6 py-14 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-2xl bg-amber-100 text-amber-700">
        <AlertCircle className="h-6 w-6" />
      </div>
      <h1 className="font-display text-2xl font-semibold text-amber-900">
        Cuenta incorrecta
      </h1>
      <p className="max-w-md text-amber-900/80">
        Esta invitación es para <strong>{expectedEmail}</strong>, pero estás en
        sesión con <strong>{currentEmail}</strong>. Cierra sesión y vuelve a
        entrar con la cuenta correcta.
      </p>
      <button
        onClick={async () => {
          await signOut();
          window.location.reload();
        }}
        className="mt-2 inline-flex items-center gap-2 rounded-full bg-amber-600 px-6 py-3 text-sm font-semibold text-white hover:bg-amber-700"
      >
        Cerrar sesión
      </button>
    </div>
  );
}
